import React, { useState, useRef, useEffect } from "react";
import { checkVideoFile } from "../utils/videoFileCheck";
import { X, Upload, Plus, Film, Trash2, CheckCircle, Clock, XCircle, MessageSquare, ChevronDown, ChevronUp } from "lucide-react";
import { GOLD } from "../constants/theme";
import { supabase } from "../../supabaseClient";
import { Select } from "../shared/Pickers";

const CATEGORIES = [
  "Finishing","Shooting","Handles","Defense","1v1",
  "Offense","Transition","5v5","Competitive",
  "Development","Psychology","Methodology",
];
const LEVELS = ["All Levels","Youth","Beginner","Intermediate","Advanced","Pro"];

const STATUS_CONFIG = {
  approved: { color: "#5AB584", bg: "#5AB58418", icon: CheckCircle, label: "Approved" },
  denied: { color: "#E06060", bg: "#E0606018", icon: XCircle, label: "Denied" },
  pending: { color: GOLD, bg: `${GOLD}18`, icon: Clock, label: "In Review" },
};

/** fetch that gives up. A request that never settles must not hold the
    modal hostage, which is what the hour on "Processing..." was. */
function fetchWithTimeout(url, opts, ms) {
  const ctl = typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = setTimeout(() => ctl && ctl.abort(), ms);
  return fetch(url, { ...opts, ...(ctl ? { signal: ctl.signal } : {}) })
    .finally(() => clearTimeout(timer));
}

/** The signed-in coach's access token, without waiting forever. Supabase's
    getSession takes a Web Lock that Safari can wedge (see supabaseClient.js),
    so it gets five seconds and then we read the stored session directly. */
async function accessToken() {
  try {
    const timeout = new Promise(r => setTimeout(() => r(null), 5000));
    const res = await Promise.race([supabase.auth.getSession(), timeout]);
    const t = res?.data?.session?.access_token;
    if (t) return t;
  } catch { /* fall through */ }
  try {
    for (let i = 0; i < window.localStorage.length; i++) {
      const k = window.localStorage.key(i);
      if (k && k.startsWith("sb-") && k.endsWith("-auth-token")) {
        const v = JSON.parse(window.localStorage.getItem(k) || "null");
        const t = v?.access_token || v?.currentSession?.access_token;
        if (t) return t;
      }
    }
  } catch { /* storage blocked */ }
  return null;
}

export function UploadPortal({ C, dark, onClose, profile }) {
  // Escape key to close
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const videoRef = useRef(null);
  const [video, setVideo] = useState(null);
  const [videoFile, setVideoFile] = useState(null);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [level, setLevel] = useState("");
  const [desc, setDesc] = useState("");
  const [purpose, setPurpose] = useState("");
  const [innovation, setInnovation] = useState("");
  const [keyPoints, setKeyPoints] = useState([""]);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [tab, setTab] = useState("upload"); // "upload" | "submissions"
  const [expandedFeedback, setExpandedFeedback] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStage, setUploadStage] = useState(""); // "", "uploading", "processing"
  const [submitError, setSubmitError] = useState("");

  // Load coach's past submissions
  const [submissions, setSubmissions] = useState([]);
  useEffect(() => {
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (data?.user) {
        const { data: subs } = await supabase.from("video_submissions")
          .select("*").eq("coach_id", data.user.id).order("created_at", { ascending: false });
        if (subs) setSubmissions(subs);
      }
    })();
  }, []);

  // Checked on pick, so a file Mux would reject never uploads at all
  // (utils/videoFileCheck.js). "checking" while the browser reads it.
  const [videoProblem, setVideoProblem] = useState(null);
  const pickedRef = useRef(null);
  const handleVideo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setVideoFile(file);
    setVideo({ name: file.name, size: file.size, type: file.type, url: URL.createObjectURL(file) });
    e.target.value = "";
    setVideoProblem("checking");
    pickedRef.current = file;
    checkVideoFile(file).then(problem => {
      if (pickedRef.current === file) setVideoProblem(problem);
    });
  };

  const fmtSize = (bytes) => {
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const addKeyPoint = () => setKeyPoints((p) => [...p, ""]);
  const updateKeyPoint = (i, v) => setKeyPoints((p) => p.map((kp, j) => (j === i ? v : kp)));
  const removeKeyPoint = (i) => setKeyPoints((p) => p.filter((_, j) => j !== i));

  const validate = () => {
    const errs = {};
    if (!video) errs.video = true;
    if (!title.trim()) errs.title = true;
    if (!category) errs.category = true;
    if (!level) errs.level = true;
    if (!desc.trim()) errs.desc = true;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    if (!videoFile) { setErrors(e => ({ ...e, video: true })); return; }
    if (videoProblem && videoProblem !== "checking") { setErrors(e => ({ ...e, video: true })); return; }
    setUploading(true); setSubmitError(""); setUploadStage("uploading"); setUploadProgress(0);

    // Every step has a deadline. The old flow awaited Mux's encoding and then
    // a browser-side insert with no timeout on either, so one request that
    // never came back (a sleeping tab, Safari's auth lock) left coaches on
    // "Processing..." for an hour with nothing saved.
    let uploaded = false;
    try {
      // 0. Who's asking. Read before the upload, while it's cheap to fail.
      const token = await accessToken();
      if (!token) throw new Error("Couldn't confirm you're signed in. Refresh the page and try again.");

      // 1. Create Mux direct upload
      const createResp = await fetchWithTimeout("/api/create-mux-upload", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ metadata: { title: title.trim(), category, level } }),
      }, 20000);
      if (!createResp.ok) {
        const j = await createResp.json().catch(() => ({}));
        throw new Error(j.error && createResp.status === 403 ? j.error : "Failed to initialize upload");
      }
      const { id: uploadId, url: uploadUrl } = await createResp.json();

      // 2. Direct PUT to Mux with progress
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener("progress", e => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        });
        xhr.addEventListener("load", () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`Upload failed: ${xhr.status}`));
        });
        xhr.addEventListener("error", () => reject(new Error("Upload failed. Check your connection and try again.")));
        xhr.addEventListener("abort", () => reject(new Error("Upload was interrupted. Try again.")));
        xhr.open("PUT", uploadUrl);
        xhr.send(videoFile);
      });
      uploaded = true;

      // 3. Save the submission. The file is with Mux now and encodes on
      //    their side whether or not this page stays open, so this only
      //    waits for the asset to exist, which is a second or two.
      setUploadStage("processing");
      const payload = {
        uploadId,
        title: title.trim(), description: desc.trim(),
        purpose: purpose.trim(), innovation: innovation.trim(),
        keyPoints: keyPoints.filter(k => k.trim()),
        category, level,
        coachName: profile?.name || "",
      };
      let inserted = null;
      let authToken = token;
      for (let i = 0; i < 30 && !inserted; i++) {
        if (i > 0) await new Promise(r => setTimeout(r, 2000));
        let resp;
        try {
          resp = await fetchWithTimeout("/api/admin-actions?action=submit-upload", {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${authToken}` },
            body: JSON.stringify(payload),
          }, 20000);
        } catch { continue; } // timed out or dropped: ask again
        if (resp.status === 401) { authToken = (await accessToken()) || authToken; continue; }
        if (!resp.ok) continue;
        const j = await resp.json().catch(() => ({}));
        if (j.ok && j.submission) { inserted = j.submission; break; }
        if (j.pending) continue;
        throw new Error(j.error || "Couldn't save your submission");
      }
      if (!inserted) throw new Error("Your video uploaded but we couldn't save the submission. Try submitting again.");

      // 4. Refresh submissions + switch to submissions tab + clear form
      setSubmissions(prev => [inserted, ...prev.filter(s => s.id !== inserted.id)]);
      setSubmitted(true);
      setTab("submissions");
      setVideo(null); setVideoFile(null); setTitle(""); setCategory(""); setLevel(""); setDesc(""); setPurpose(""); setInnovation(""); setKeyPoints([""]);
    } catch (e) {
      const msg = e?.name === "AbortError"
        ? (uploaded ? "Your video uploaded but saving timed out. Try submitting again." : "The connection timed out. Try again.")
        : (e?.message || "Upload failed");
      setSubmitError(msg);
    } finally {
      setUploading(false); setUploadStage("");
    }
  };

  const inputStyle = (hasError) => ({
    width: "100%",
    background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
    border: `1px solid ${hasError ? "#E06060" : dark ? "rgba(255,255,255,0.08)" : "#ddd"}`,
    borderRadius: 8,
    padding: "10px 12px",
    fontSize: 13,
    color: C.text,
    outline: "none",
    fontFamily: "inherit",
    boxSizing: "border-box",
  });

  const selectStyle = (hasError) => ({
    ...inputStyle(hasError),
    appearance: "none",
    WebkitAppearance: "none",
    paddingRight: 32,
  });

  const labelStyle = {
    fontSize: 11,
    fontWeight: 700,
    color: C.textMid,
    textTransform: "uppercase",
    letterSpacing: 0.5,
    marginBottom: 6,
    display: "block",
  };

  /* ── Success state ── */
  if (submitted) {
    return (
      <>
        <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", zIndex: 9999 }} />
        <div style={{ position: "fixed", inset: 0, margin: "auto", width: 420, height: "fit-content", maxWidth: "90vw", background: dark ? "#151515" : "#fff", borderRadius: 16, zIndex: 10000, padding: 40, textAlign: "center", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "#eee"}`, boxShadow: dark ? "0 30px 80px rgba(0,0,0,0.7)" : "0 20px 60px rgba(0,0,0,0.15)", animation: "popIn .25s ease both" }}>
          <div style={{ width: 64, height: 64, borderRadius: "50%", background: `${GOLD}20`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <CheckCircle size={32} color={GOLD} />
          </div>
          <div style={{ fontSize: 20, fontWeight: 700, color: C.text, marginBottom: 8 }}>Submitted for Review</div>
          <div style={{ fontSize: 13, color: C.textMid, lineHeight: 1.6, maxWidth: 300, margin: "0 auto" }}>
            The BAM team will review it. If it's approved, it goes in the library with your name on it, and you'll hear either way.
          </div>
          <div style={{ display: "flex", gap: 10, marginTop: 24, justifyContent: "center" }}>
            <button onClick={() => { setSubmitted(false); setVideo(null); setTitle(""); setCategory(""); setLevel(""); setDesc(""); setKeyPoints([""]); }} style={{ padding: "10px 24px", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.textMid, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>Submit Another</button>
            <button onClick={onClose} style={{ padding: "10px 28px", borderRadius: 8, border: "none", background: GOLD, color: "#111", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>Done</button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(8px)", WebkitBackdropFilter: "blur(8px)", zIndex: 9999 }} />
      <div style={{ position: "fixed", inset: 0, margin: "auto", width: 600, height: "85vh", maxWidth: "92vw", background: dark ? "#151515" : "#fff", borderRadius: 16, zIndex: 10000, display: "flex", flexDirection: "column", overflow: "hidden", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "#eee"}`, boxShadow: dark ? "0 30px 80px rgba(0,0,0,0.7)" : "0 20px 60px rgba(0,0,0,0.15)", animation: "popIn .25s ease both" }}>

        {/* Header */}
        <div style={{ padding: "18px 24px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <Upload size={16} color={GOLD} />
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text }}>Submit Content</span>
          </div>
          <div onClick={onClose} role="button" tabIndex={0} aria-label="Close upload panel" style={{ width: 30, height: 30, borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)" }}>
            <X size={14} color={C.textMid} aria-hidden="true" />
          </div>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
          {[["upload", "Upload Video"], ["submissions", "My Submissions"]].map(([key, label]) => (
            <div key={key} onClick={() => setTab(key)} style={{ flex: 1, textAlign: "center", padding: "11px 0", fontSize: 13, fontWeight: 700, cursor: "pointer", color: tab === key ? (dark ? GOLD : "#111") : C.textDim, borderBottom: tab === key ? `2px solid ${GOLD}` : "2px solid transparent", transition: "all .15s" }}>
              {label}
            </div>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: "auto", padding: "20px 24px" }}>

          {tab === "upload" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

              {/* Video Upload */}
              <div>
                <label style={labelStyle}>Video File <span style={{ color: "#E06060" }}>*</span></label>
                <input ref={videoRef} type="file" accept="video/*" onChange={handleVideo} style={{ display: "none" }} />
                {video ? (
                  <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderRadius: 8, background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)", border: `1px solid ${dark ? "rgba(255,255,255,0.08)" : "#ddd"}` }}>
                    <Film size={16} color={GOLD} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{video.name}</div>
                      <div style={{ fontSize: 11, color: C.textDim }}>{fmtSize(video.size)}</div>
                    </div>
                    <div onClick={() => { setVideo(null); setVideoFile(null); setVideoProblem(null); pickedRef.current = null; }} style={{ cursor: "pointer", padding: 4 }}>
                      <Trash2 size={13} color={C.textDim} />
                    </div>
                  </div>
                ) : null}
                {video && videoProblem && videoProblem !== "checking" && (
                  <div role="alert" style={{ marginTop: 8, fontSize: 12.5, lineHeight: 1.5, color: "#E06060" }}>
                    {videoProblem} <span onClick={() => videoRef.current?.click()} style={{ textDecoration: "underline", cursor: "pointer", fontWeight: 700 }}>Pick another file</span>
                  </div>
                )}
                {video ? null : (
                  <div onClick={() => videoRef.current?.click()}
                    style={{ padding: "24px 20px", borderRadius: 10, border: `2px dashed ${errors.video ? "#E06060" : dark ? "rgba(255,255,255,0.1)" : "#ddd"}`, display: "flex", flexDirection: "column", alignItems: "center", gap: 8, cursor: "pointer", transition: "border-color .15s" }}>
                    <Upload size={20} color={C.textDim} />
                    <div style={{ fontSize: 13, color: C.textMid, fontWeight: 500 }}>Click to upload video</div>
                    <div style={{ fontSize: 11, color: C.textDim }}>MP4, MOV, or WebM</div>
                  </div>
                )}
              </div>

              {/* Submission criteria — shown, not enforced. */}
              <div style={{ padding: "12px 14px", borderRadius: 10, background: `${GOLD}0e`, border: `1px solid ${GOLD}44` }}>
 <div style={{ fontSize: 10, fontWeight: 800, color: GOLD, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>Before you upload: quick criteria</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5, fontSize: 12, color: C.textMid, lineHeight: 1.55 }}>
                  <div>1. It's not already on the platform.</div>
 <div>2. It isn't an age-old drill with no innovation: everyone knows the classics. We're here to innovate.</div>
                  <div>3. It has a specific purpose you can explain.</div>
                  <div>4. It's something other coaches would actually want to use in practice.</div>
                </div>
              </div>

              {/* Title */}
              <div>
                <label style={labelStyle}>Title <span style={{ color: "#E06060" }}>*</span></label>
                <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Euro Step Finishing Under Contact" style={inputStyle(errors.title)} />
              </div>

              {/* Category + Level row */}
              <div style={{ display: "flex", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Category <span style={{ color: "#E06060" }}>*</span></label>
                  <Select value={category} onChange={(e) => setCategory(e.target.value)} style={selectStyle(errors.category)}>
                    <option value="" disabled>Select...</option>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </Select>
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>Level <span style={{ color: "#E06060" }}>*</span></label>
                  <Select value={level} onChange={(e) => setLevel(e.target.value)} style={selectStyle(errors.level)}>
                    <option value="" disabled>Select...</option>
                    {LEVELS.map((l) => <option key={l} value={l}>{l}</option>)}
                  </Select>
                </div>
              </div>

              {/* Description */}
              <div>
                <label style={labelStyle}>Description <span style={{ color: "#E06060" }}>*</span></label>
                <textarea value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Describe the drill, progression, or concept..." rows={3} style={{ ...inputStyle(errors.desc), resize: "vertical", minHeight: 72 }} />
              </div>

              {/* Specific purpose — criteria #3 */}
              <div>
                <label style={labelStyle}>Specific purpose</label>
                <textarea value={purpose} onChange={(e) => setPurpose(e.target.value)}
                  placeholder="What does this drill specifically train? (e.g., making quick decisions on close-outs from the wing)"
                  rows={2}
                  style={{ ...inputStyle(false), resize: "vertical", minHeight: 56 }} />
              </div>

              {/* Innovation — criteria #2 */}
              <div>
                <label style={labelStyle}>What makes this different from the classic version?</label>
                <textarea value={innovation} onChange={(e) => setInnovation(e.target.value)}
                  placeholder="How have you progressed or tweaked this beyond the standard drill?"
                  rows={2}
                  style={{ ...inputStyle(false), resize: "vertical", minHeight: 56 }} />
              </div>

              {/* Key Coaching Points */}
              <div>
                <label style={labelStyle}>Key Coaching Points</label>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  {keyPoints.map((kp, i) => (
                    <div key={i} style={{ display: "flex", gap: 8, alignItems: "center" }}>
                      <input value={kp} onChange={(e) => updateKeyPoint(i, e.target.value)} placeholder={`Point ${i + 1}`} style={{ ...inputStyle(false), flex: 1 }} />
                      {keyPoints.length > 1 && (
                        <div onClick={() => removeKeyPoint(i)} style={{ cursor: "pointer", padding: 4, flexShrink: 0 }}>
                          <X size={12} color={C.textDim} />
                        </div>
                      )}
                    </div>
                  ))}
                  {keyPoints.length < 6 && (
                    <div onClick={addKeyPoint} style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer", padding: "4px 0" }}>
                      <Plus size={12} color={GOLD} />
                      <span style={{ fontSize: 12, color: GOLD, fontWeight: 600 }}>Add point</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            /* ── Submissions Tab ── */
            <div>
              {submissions.length === 0 && (
                <div style={{ padding: "28px 0", textAlign: "center", fontSize: 13, color: C.textDim }}>Nothing submitted yet.</div>
              )}
              {/* Submissions list */}
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {submissions.map((s) => {
                  const sc = STATUS_CONFIG[s.status];
                  const StatusIcon = sc.icon;
                  const isExpanded = expandedFeedback === s.id;
                  return (
                    <div key={s.id} style={{ borderRadius: 10, border: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "#eee"}`, overflow: "hidden" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: dark ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.02)", cursor: s.feedback ? "pointer" : "default" }}
                        onClick={() => s.feedback && setExpandedFeedback(isExpanded ? null : s.id)}>
                        <StatusIcon size={15} color={sc.color} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title}</div>
                          <div style={{ fontSize: 11, color: C.textDim, marginTop: 2 }}>{s.category} · {s.created_at ? new Date(s.created_at).toLocaleDateString() : s.date}</div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: sc.color, background: sc.bg, padding: "3px 10px", borderRadius: 6 }}>{sc.label}</div>
                          {s.feedback && (isExpanded ? <ChevronUp size={12} color={C.textDim} /> : <ChevronDown size={12} color={C.textDim} />)}
                        </div>
                      </div>
                      {s.feedback && isExpanded && (
                        <div style={{ padding: "12px 14px", borderTop: `1px solid ${dark ? "rgba(255,255,255,0.06)" : "#eee"}`, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.01)" }}>
                          <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                            <MessageSquare size={13} color={C.textMid} style={{ marginTop: 2, flexShrink: 0 }} />
                            <div style={{ fontSize: 12, color: C.textMid, lineHeight: 1.6 }}>{s.feedback}</div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

            </div>
          )}
        </div>

        {/* Footer — only show on upload tab */}
        {tab === "upload" && (
          <div style={{ padding: "16px 24px", borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
            {uploading && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: C.textMid, marginBottom: 6 }}>
                  <span>{uploadStage === "uploading" ? `Uploading... ${uploadProgress}%` : "Saving your submission..."}</span>
                </div>
                <div style={{ height: 6, background: dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)", borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ height: 6, background: GOLD, borderRadius: 3, width: `${uploadStage === "uploading" ? uploadProgress : 100}%`, transition: "width .3s", animation: uploadStage === "processing" ? "pulse 1.5s infinite" : "none" }} />
                </div>
              </div>
            )}
            {submitError && <div style={{ fontSize: 12, color: "#E06060", marginBottom: 10 }}>{submitError}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={onClose} disabled={uploading} style={{ flex: 1, padding: "11px 0", borderRadius: 8, border: `1px solid ${C.border}`, background: "transparent", color: C.textMid, fontSize: 13, fontWeight: 600, cursor: uploading ? "not-allowed" : "pointer", fontFamily: "inherit", opacity: uploading ? 0.5 : 1 }}>Cancel</button>
              <button onClick={handleSubmit} disabled={uploading} style={{ flex: 2, padding: "11px 0", borderRadius: 8, border: "none", background: uploading ? "rgba(226,221,159,0.3)" : GOLD, color: "#111", fontSize: 13, fontWeight: 700, cursor: uploading ? "not-allowed" : "pointer", fontFamily: "inherit", boxShadow: uploading ? "none" : `0 2px 12px ${GOLD}30` }}>
                {uploading ? (uploadStage === "uploading" ? "Uploading..." : "Saving...") : "Submit for Review"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
